import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Popconfirm,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { FormInstance } from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { userService, type User } from '@/services/user.service';
import {
  mocService,
  MOC_HANDOVER_REASONS,
  type MocHandoverItem,
  type MocHandoverReason,
  type MocHandoverView,
} from '@/services/moc.service';

const { Text } = Typography;

function userLabel(u: Pick<User, 'first_name' | 'last_name' | 'email' | 'username' | 'is_active'>): string {
  const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.username;
  return `${name} (${u.email})${u.is_active ? '' : ' — inactive'}`;
}

function useUserOptions() {
  const { data: users = [], isLoading } = useQuery({
    queryKey: ['moc-handover-users'],
    queryFn: () => userService.getAll({}),
    staleTime: 5 * 60_000,
  });
  const all = useMemo(
    () => users.map((u) => ({ value: u.id, label: userLabel(u) })).sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );
  const active = useMemo(
    () =>
      users
        .filter((u) => u.is_active)
        .map((u) => ({ value: u.id, label: userLabel(u) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [users]
  );
  return { all, active, isLoading };
}

export interface HandoverFormValues {
  handover_from_user_id?: string;
  handover_to_user_id?: string;
  handover_reason?: MocHandoverReason;
  handover_dates?: [dayjs.Dayjs, dayjs.Dayjs];
}

export function handoverPayloadFromForm(values: HandoverFormValues) {
  const reason = values.handover_reason || 'resignation';
  const [from, to] = values.handover_dates || [];
  return {
    from_user_id: String(values.handover_from_user_id || ''),
    to_user_id: String(values.handover_to_user_id || ''),
    reason,
    effective_from: reason === 'leave' && from ? from.format('YYYY-MM-DD') : null,
    effective_to: reason === 'leave' && to ? to.format('YYYY-MM-DD') : null,
  };
}

/** Handover inputs; render inside an antd Form. */
export const MocHandoverFields: React.FC<{ form: FormInstance; disabled?: boolean }> = ({ form, disabled }) => {
  const { all, active, isLoading } = useUserOptions();
  const reason = Form.useWatch('handover_reason', form) as MocHandoverReason | undefined;
  const fromUser = Form.useWatch('handover_from_user_id', form) as string | undefined;
  return (
    <>
      <Form.Item
        name="handover_from_user_id"
        label="User handing over"
        rules={[{ required: true, message: 'Select the user going on leave / leaving' }]}
      >
        <Select showSearch optionFilterProp="label" options={all} loading={isLoading} disabled={disabled} />
      </Form.Item>
      <Form.Item name="handover_reason" label="Reason" rules={[{ required: true, message: 'Reason is required' }]}>
        <Select options={MOC_HANDOVER_REASONS.map((r) => ({ value: r.value, label: r.label }))} disabled={disabled} />
      </Form.Item>
      <Form.Item
        name="handover_to_user_id"
        label={reason === 'leave' ? 'Delegate (covers approvals during leave)' : 'Successor'}
        rules={[
          { required: true, message: 'Select the successor / delegate' },
          {
            validator: (_, v) =>
              v && v === fromUser ? Promise.reject(new Error('Must be a different user')) : Promise.resolve(),
          },
        ]}
      >
        <Select showSearch optionFilterProp="label" options={active} loading={isLoading} disabled={disabled} />
      </Form.Item>
      {reason === 'leave' && (
        <Form.Item
          name="handover_dates"
          label="Leave dates (delegation window)"
          rules={[{ required: true, message: 'Leave dates are required' }]}
        >
          <DatePicker.RangePicker style={{ width: '100%' }} disabled={disabled} />
        </Form.Item>
      )}
    </>
  );
};

/** Impact list with optional-item checkboxes. */
export const MocHandoverImpactTable: React.FC<{
  items: MocHandoverItem[];
  temporary: boolean;
  editable: boolean;
  executedView?: boolean;
  onToggle?: (item: MocHandoverItem, selected: boolean) => void;
}> = ({ items, temporary, editable, executedView, onToggle }) => {
  if (!items.length) return <Empty description="Nothing assigned to this user" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  return (
    <Table<MocHandoverItem>
      size="small"
      rowKey={(r) => `${r.item_type}:${r.ref_id}`}
      dataSource={items}
      pagination={items.length > 15 ? { pageSize: 15 } : false}
      columns={[
        {
          title: executedView ? 'Moved' : 'Move',
          width: 80,
          render: (_, r) =>
            executedView ? (
              r.executed ? (
                <Tag color="green">Done</Tag>
              ) : r.error ? (
                <Tag color="red">Failed</Tag>
              ) : (
                <Tag>Skipped</Tag>
              )
            ) : temporary ? (
              <Tag color="blue">Delegated</Tag>
            ) : (
              <Checkbox
                checked={r.mandatory || r.selected}
                disabled={r.mandatory || !editable}
                onChange={(e) => onToggle?.(r, e.target.checked)}
              />
            ),
        },
        { title: 'Type', dataIndex: 'item_label', width: 220 },
        { title: 'Record', dataIndex: 'label', ellipsis: true },
        ...(executedView
          ? [
              {
                title: 'Note',
                render: (_: unknown, r: MocHandoverItem) =>
                  r.error ? <Text type="danger">{r.error}</Text> : r.executed_at ? dayjs(r.executed_at).format('DD MMM YYYY HH:mm') : '',
              },
            ]
          : []),
      ]}
    />
  );
};

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  planned: { color: 'default', label: 'Planned — runs on Start implementation' },
  executed: { color: 'green', label: 'Handed over' },
  active: { color: 'blue', label: 'Delegation active' },
  ended: { color: 'default', label: 'Delegation ended' },
};

/** MOC detail: Key Personnel responsibility handover. */
export const MocHandoverSection: React.FC<{
  mocId: string;
  mocStatus: string;
  canEdit: boolean;
  canExecute: boolean;
}> = ({ mocId, mocStatus, canEdit, canExecute }) => {
  const [form] = Form.useForm<HandoverFormValues>();
  const [view, setView] = useState<MocHandoverView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selection, setSelection] = useState<Record<string, boolean>>({});
  const [extendTo, setExtendTo] = useState<dayjs.Dayjs | null>(null);

  const watchedReason = Form.useWatch('handover_reason', form) as MocHandoverReason | undefined;
  const editableStatus = mocStatus === 'draft' || mocStatus === 'submitted';
  const editable = canEdit && editableStatus;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const v = await mocService.getHandover(mocId);
      setView(v);
      setSelection({});
      if (v.handover && v.handover.status === 'planned') {
        form.setFieldsValue({
          handover_from_user_id: v.handover.from_user_id,
          handover_to_user_id: v.handover.to_user_id,
          handover_reason: v.handover.reason,
          handover_dates:
            v.handover.effective_from && v.handover.effective_to
              ? [dayjs(v.handover.effective_from), dayjs(v.handover.effective_to)]
              : undefined,
        });
      }
      setExtendTo(v.handover?.effective_to ? dayjs(v.handover.effective_to) : null);
    } catch {
      message.error('Could not load the responsibility handover');
    } finally {
      setLoading(false);
    }
  }, [mocId, form]);

  useEffect(() => {
    void load();
  }, [load]);

  const items = useMemo(
    () =>
      (view?.items || []).map((it) => {
        const key = `${it.item_type}:${it.ref_id}`;
        return key in selection ? { ...it, selected: selection[key] } : it;
      }),
    [view, selection]
  );

  const save = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const payload = handoverPayloadFromForm(values);
      const v = await mocService.saveHandover(mocId, {
        ...payload,
        items: items.filter((i) => !i.mandatory).map((i) => ({ item_type: i.item_type, ref_id: i.ref_id, selected: i.selected })),
      });
      setView(v);
      setSelection({});
      message.success('Handover saved');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not save the handover');
    } finally {
      setSaving(false);
    }
  };

  const rerun = async () => {
    setSaving(true);
    try {
      setView(await mocService.rerunHandover(mocId));
      message.success('Handover re-run');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not re-run the handover');
    } finally {
      setSaving(false);
    }
  };

  const extend = async () => {
    if (!extendTo) return;
    setSaving(true);
    try {
      setView(await mocService.extendHandover(mocId, extendTo.format('YYYY-MM-DD')));
      message.success('Delegation end date updated');
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not update the end date');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card title="Responsibility handover">
        <Spin />
      </Card>
    );
  }
  if (view && !view.available) {
    return (
      <Card title="Responsibility handover">
        <Alert type="info" showIcon message="Responsibility handover becomes available after the latest update is deployed." />
      </Card>
    );
  }

  const h = view?.handover || null;
  const temporary = h && h.status !== 'planned' ? h.handover_kind === 'temporary' : (watchedReason ?? h?.reason) === 'leave';
  const executed = h && h.status !== 'planned';
  const statusTag = h ? STATUS_TAG[h.status] : null;

  return (
    <Card
      title="Responsibility handover"
      extra={statusTag ? <Tag color={statusTag.color}>{statusTag.label}</Tag> : null}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          Leave: the delegate can approve the user&apos;s items during the leave dates; nothing is reassigned. Resignation /
          termination: pending approvals, workflow approver slots, department Process Owner and the ticked records move to
          the successor when implementation starts.
        </Text>

        {executed && h ? (
          <Descriptions size="small" column={2} bordered>
            <Descriptions.Item label="From">{h.from_user_name || h.from_user_id}</Descriptions.Item>
            <Descriptions.Item label={h.handover_kind === 'temporary' ? 'Delegate' : 'Successor'}>
              {h.to_user_name || h.to_user_id}
            </Descriptions.Item>
            <Descriptions.Item label="Reason">{h.reason}</Descriptions.Item>
            <Descriptions.Item label="Executed">
              {h.executed_at ? dayjs(h.executed_at).format('DD MMM YYYY HH:mm') : '—'}
            </Descriptions.Item>
            {h.handover_kind === 'temporary' && (
              <Descriptions.Item label="Delegation window" span={2}>
                {h.effective_from} to {h.effective_to}
              </Descriptions.Item>
            )}
          </Descriptions>
        ) : (
          <Form form={form} layout="vertical" requiredMark="optional" initialValues={{ handover_reason: 'resignation' }}>
            <MocHandoverFields form={form} disabled={!editable} />
          </Form>
        )}

        {(view?.warnings || []).map((w) => (
          <Alert key={w} type="warning" showIcon message={w} />
        ))}

        {h ? (
          <MocHandoverImpactTable
            items={items}
            temporary={Boolean(temporary)}
            editable={editable}
            executedView={Boolean(executed)}
            onToggle={(it, selected) => setSelection((s) => ({ ...s, [`${it.item_type}:${it.ref_id}`]: selected }))}
          />
        ) : (
          <Text type="secondary">Save the handover to see everything that will be handed over.</Text>
        )}

        <Space wrap>
          {editable && (
            <Button type="primary" onClick={() => void save()} loading={saving}>
              Save handover
            </Button>
          )}
          {canExecute && h && h.handover_kind === 'permanent' && mocStatus === 'implementation_in_progress' && (
            <Popconfirm title="Move anything still assigned to the previous user?" onConfirm={() => void rerun()}>
              <Button loading={saving}>Re-run handover</Button>
            </Popconfirm>
          )}
          {canEdit && h && h.handover_kind === 'temporary' && h.status === 'active' && (
            <>
              <DatePicker value={extendTo} onChange={(d) => setExtendTo(d)} />
              <Button onClick={() => void extend()} loading={saving} disabled={!extendTo}>
                Change delegation end date
              </Button>
            </>
          )}
        </Space>
      </Space>
    </Card>
  );
};

export default MocHandoverSection;
